import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import axios from "axios";

// Validation schemas for each step
const stepSchemas = [
  // Step 1 Schema: Personal Information
  yup.object().shape({
    firstName: yup.string().required("First Name is required"),
    middleName: yup.string(),
    lastName: yup.string().required("Last Name is required"),
    religiousName: yup.string(),
    gender: yup.string().required("Gender is required"),
    nationality: yup.string().required("Nationality is required"),
    dob: yup
      .date()
      .typeError("Please enter a valid date")
      .max(new Date(), "Date of birth cannot be in the future")
      .required("Date of birth is required")
      .test("age", "You must be at least 18 years old", function (value) {
        if (!value) return false;
        const cutoff = new Date();
        cutoff.setFullYear(cutoff.getFullYear() - 18);
        return value <= cutoff;
      }),
    primaryMobile: yup
      .string()
      .matches(/^\+?[1-9]\d{1,14}$/, "Invalid phone number")
      .required("Primary Mobile is required"),
    alternateMobile: yup.string(),
    preferredCommunication: yup.string().required("Communication is required"),
    email: yup.string().email("Invalid email format").required("Email is required"),
    communicationAddress: yup.string().required("Communication Address is required"),
    permanentAddress: yup.string(),
  }),
  // Step 2 Schema: Emergency Contact & Kutiya Details
  yup.object().shape({
    emergencyContactName: yup.string().required("Emergency Contact Name is required"),
    emergencyContactNumber: yup
      .string()
      .matches(/^\+?[1-9]\d{1,14}$/, "Invalid phone number")
      .required("Emergency Contact Number is required"),
    typeOfKutiya: yup.string().required("Type of Kutiya is required"),
    numberOfPersons: yup
      .number()
      .required("Number of Persons is required")
      .positive("Number of persons must be positive")
      .integer("Number of persons must be whole number"),
    arrivalDate: yup
      .date()
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .required("Arrival Date is required"),

    departureDate: yup
      .date()
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .required("Departure Date is required")
      .min(yup.ref("arrivalDate"), "Departure date must be after arrival date"),

    proximityToSwamiJi: yup
      .boolean()
      .transform((value, originalValue) => {
        if (originalValue === "true") return true;
        if (originalValue === "false") return false;
        return undefined; // fallback so Yup can mark it as required
      })
      .required("Please select proximity preference"),
    specialRequirements: yup.string(),
  }),
  // Step 3 Group & Volunteering:
  yup.object().shape({
    groupRegister: yup.boolean().required("Invalid input for group registration"),
    volunteeringRegistration: yup.boolean().required("Invalid input for voluntreer registration"),
    commentsForOrganizers: yup.string(),
  }),
  // Step 4 Schema: Document Upload & Payment
  yup.object().shape({
    documentId: yup.string().required("Document ID is required"),
    idDoc: yup.mixed().required("Document upload is required"),
    paymentMethod: yup.string().required("Please select a payment method"),
    paymentAcknowledgment: yup
      .boolean()
      .oneOf([true], "You must acknowledge the payment terms")
      .required("You must acknowledge the payment terms"),
    termsAccepted: yup
      .boolean()
      .oneOf([true], "You must accept the terms and conditions")
      .required("You must accept the terms and conditions"),
  }),
];

// Add this required field indicator component at the top of your file
const RequiredIndicator = () => (
  <span className="text-red-500 ml-1" aria-label="required">
    *
  </span>
);

const calculateCost = (
  kutiyaType,
  numberOfPersons,
  arrivalDate,
  departureDate,
  proximityToSwamiJi
) => {
  if (!kutiyaType || !numberOfPersons || !arrivalDate || !departureDate) {
    return 0;
  }

  const start = new Date(arrivalDate);
  const end = new Date(departureDate);
  const numberOfDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));

  const rates = {
    ganga: {
      base: 7500, // per day rate
      maxPersons: 3,
    },
    yamuna: {
      base: 9000, // per day rate
      maxPersons: 6,
      perPerson: 2000, // per person per day if exceeding maxPersons
    },
    saraswati: {
      base: 12000, // per day rate
      maxPersons: 12,
      perPerson: 1500, // per person per day if exceeding maxPersons
    },
  };

  const rate = rates[kutiyaType];
  if (!rate) return 0;

  let totalCost = 0;

  // Calculate base cost per day
  if (numberOfPersons <= rate.maxPersons) {
    totalCost = rate.base * numberOfDays;
  } else if (rate.perPerson) {
    totalCost = numberOfPersons * rate.perPerson * numberOfDays;
  }

  // Add proximity cost (2000 per day) if selected
  // Fix: Convert string "true" to boolean or directly compare with string
  if (proximityToSwamiJi === true || proximityToSwamiJi === "true") {
    totalCost += 2000 * numberOfDays;
  }

  return totalCost;
};

const MultiStepForm = () => {
  const today = new Date();
  const formattedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(today.getDate()).padStart(2, "0")}`;

  const [step, setStep] = useState(0);
  const [isSameAddress, setIsSameAddress] = useState(false);
  const [formData, setFormData] = useState({
    isGroup: "no",
    groupMembers: [],
    isVolunteer: "no",
    volunteerAreas: [],
    otherVolunteerArea: "",
    organizerComments: "",
    termsAccepted: false,
    paymentMethod: "",
    paymentAcknowledgment: false,
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
    setValue,
    getValues,
  } = useForm({
    resolver: yupResolver(stepSchemas[step]),
    mode: "onChange",
  });

  useEffect(() => {
    const totalCost = calculateCost(
      getValues("typeOfKutiya"),
      Number(getValues("numberOfPersons")),
      getValues("arrivalDate"),
      getValues("departureDate")
    );
    setFormData((prev) => ({ ...prev, totalCost }));
  }, [
    getValues("typeOfKutiya"),
    getValues("numberOfPersons"),
    getValues("arrivalDate"),
    getValues("departureDate"),
  ]);

  const handleAddMember = () => {
    setFormData({
      ...formData,
      groupMembers: [...formData.groupMembers, { name: "", age: "", gender: "", idFile: null }],
    });
  };

  const handleRemoveMember = (index) => {
    const updatedMembers = formData.groupMembers.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      groupMembers: updatedMembers,
    });
  };

  const handleMemberChange = (index, field, value) => {
    const updatedMembers = formData.groupMembers.map((member, i) => {
      if (i === index) {
        return { ...member, [field]: value };
      }
      return member;
    });
    setFormData({
      ...formData,
      groupMembers: updatedMembers,
    });
  };

  const handleVolunteerAreaChange = (area) => {
    const updatedAreas = formData.volunteerAreas.includes(area)
      ? formData.volunteerAreas.filter((a) => a !== area)
      : [...formData.volunteerAreas, area];
    setFormData({ ...formData, volunteerAreas: updatedAreas });
  };

  const onNext = async () => {
    const isValid = await trigger();

    if (isValid) setStep(step + 1);
  };

  const onPrevious = () => setStep(step - 1);

  const onSubmit = async (data) => {
    try {
      const formData = new FormData();

      Object.keys(data).forEach((key) => {
        if (key === "idDoc") {
          formData.append("file", data[key][0]);
        } else {
          formData.append(key, data[key]);
        }
      });

      const response = await axios.post("http://localhost:5000/api/applicants/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      alert("Form submitted successfully!");
    } catch (error) {
      console.error("❌ Error:", error.response?.data || error.message);
      alert("Submission failed.");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Kumbh Mahaparv Booking</h2>
          <p className="text-lg text-gray-600">
            Complete your registration for this sacred journey
          </p>
        </div>

        {/* Existing progress steps */}
        <div className="flex w-full px-4 overflow-x-auto sm:justify-center sm:overflow-visible sm:flex-row gap-4 mb-6 pb-2">
          {[
            "Personal Information",
            "Emergency & Accommodation",
            "Group & Volunteering",
            "Verification & Payment",
          ].map((label, index) => (
            <div key={index} className="flex flex-col items-center text-center flex-shrink-0 w-28">
              <div
                className={`w-10 h-10 flex items-center justify-center rounded-full text-white font-semibold
          ${step >= index ? " bg-orange-600" : "bg-gray-300"}
        `}
              >
                {index + 1}
              </div>
              <p
                className={`mt-2 text-sm ${
                  step >= index ? "text-blue-500 font-medium" : "text-gray-500"
                }`}
              >
                {label}
              </p>
            </div>
          ))}
        </div>

        {/* Wrap your existing form in a white container */}
        <div className="w-full max-w-2xl mx-auto p-4 sm:p-6 bg-white shadow-lg rounded-lg">
          <form className="w-full space-y-6" onSubmit={handleSubmit(onSubmit)}>
            {/* Step 1: Personal Information */}
            {step === 0 && (
              <>
                <div className="space-y-6">
                  <h2 className="text-lg font-semibold border-b pb-2">Personal Information</h2>

                  {/* Name fields - stack on mobile */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        First Name <RequiredIndicator />
                      </label>
                      <input
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 text-sm"
                        {...register("firstName")}
                      />
                      {errors.firstName && (
                        <p className="mt-1 text-sm text-red-500">{errors.firstName.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Middle Name</label>
                      <input
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 text-sm"
                        {...register("middleName")}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Last Name <RequiredIndicator />
                      </label>
                      <input
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 text-sm"
                        {...register("lastName")}
                      />
                      {errors.lastName && (
                        <p className="mt-1 text-sm text-red-500">{errors.lastName.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Other fields - stack on mobile */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Spiritual/Religious Name
                      </label>
                      <input
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 text-sm"
                        {...register("religiousName")}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Gender <RequiredIndicator />
                      </label>
                      <select
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 text-sm"
                        {...register("gender")}
                      >
                        <option value="">Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="non-binary">Non Binary</option>
                        <option value="null">Prefer not to say</option>
                      </select>
                      {errors.gender && (
                        <p className="mt-1 text-sm text-red-500">{errors.gender.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Nationality <RequiredIndicator />
                      </label>
                      <input
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 text-sm"
                        {...register("nationality")}
                      />
                      {errors.nationality && (
                        <p className="mt-1 text-sm text-red-500">{errors.nationality.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Date of Birth <RequiredIndicator />
                      </label>
                      <input
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 text-sm"
                        type="date"
                        max={new Date().toISOString().split("T")[0]}
                        {...register("dob", {
                          setValueAs: (value) => (value ? new Date(value) : null),
                        })}
                      />
                      {errors.dob && (
                        <p className="mt-1 text-sm text-red-500">{errors.dob.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Primary Mobile <RequiredIndicator />
                      </label>
                      <input
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 text-sm"
                        {...register("primaryMobile")}
                      />
                      {errors.primaryMobile && (
                        <p className="mt-1 text-sm text-red-500">{errors.primaryMobile.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Alternate Mobile
                      </label>
                      <input
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 text-sm"
                        {...register("alternateMobile")}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Email <RequiredIndicator />
                      </label>
                      <input
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 text-sm"
                        type="email"
                        {...register("email")}
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Preferred Communication <RequiredIndicator />
                      </label>
                      <select
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 text-sm"
                        {...register("preferredCommunication")}
                      >
                        <option value="sms">SMS</option>
                        <option value="phone">Phone</option>
                        <option value="email">Email</option>
                      </select>
                      {errors.preferredCommunication && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.preferredCommunication.message}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Address fields - full width on mobile */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Communication Address <RequiredIndicator />
                      </label>
                      <textarea
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 text-sm"
                        rows="3"
                        {...register("communicationAddress")}
                        onChange={(e) => {
                          if (isSameAddress) {
                            setValue("permanentAddress", e.target.value);
                          }
                        }}
                      />
                      {errors.communicationAddress && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.communicationAddress.message}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="sameAddress"
                        checked={isSameAddress}
                        onChange={(e) => {
                          setIsSameAddress(e.target.checked);
                          if (e.target.checked) {
                            setValue("permanentAddress", getValues("communicationAddress"));
                          }
                        }}
                        className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                      />
                      <label htmlFor="sameAddress" className="ml-2 block text-sm text-gray-700">
                        Same as Communication Address
                      </label>
                    </div>

                    {!isSameAddress && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Permanent Address
                        </label>
                        <textarea
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 text-sm"
                          rows="3"
                          {...register("permanentAddress")}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Step 2: Emergency Contact & Kutiya */}
            {step === 1 && (
              <>
                <h2 className="text-lg font-semibold">Step 2: Emergency & Kutiya Details</h2>
                <div className=" flex gap-4 my-6 justify-between">
                  <div className="w-1/2">
                    <label htmlFor="emergencyContactName">
                      Emergency Contact Name <RequiredIndicator />
                    </label>
                    <input
                      id="emergencyContactName"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                      {...register("emergencyContactName")}
                    />
                    {errors.emergencyContactName && (
                      <p className="text-red-500">{errors.emergencyContactName.message}</p>
                    )}
                  </div>

                  <div className="w-1/2">
                    <label className="semi-bold block" htmlFor="emergencyContactNumber">
                      Emergency Contact Number
                      <RequiredIndicator />
                    </label>

                    <input
                      id="emergencyContactNumber"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                      {...register("emergencyContactNumber")}
                    />
                    {errors.emergencyContactNumber && (
                      <p className="text-red-500">{errors.emergencyContactNumber.message}</p>
                    )}
                  </div>
                </div>

                <div className="flex gap-4 my-6">
                  <div className="w-1/2">
                    <label htmlFor="typeOfKutiya">
                      Type of Kutiya <RequiredIndicator />{" "}
                    </label>
                    <select
                      className="input block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 "
                      {...register("typeOfKutiya")}
                    >
                      <option value="">Select Kutiya Type</option>
                      <option value="ganga">Ganga Kutiya (₹7500/day for 3 persons)</option>
                      <option value="yamuna">
                        Yamuna Kutiya (₹9000/day for 6 persons or ₹2000/person/day)
                      </option>
                      <option value="saraswati">
                        Saraswati Kutiya (₹12000/day for 12 persons or ₹1500/person/day)
                      </option>
                    </select>
                    {errors.typeOfKutiya && (
                      <p className="text-red-500">{errors.typeOfKutiya.message}</p>
                    )}
                  </div>
                  <div className="w-1/2">
                    <label className="semi-bold block" htmlFor="numberOfPersons">
                      Number of Persons <RequiredIndicator />
                    </label>

                    <input
                      id="numberOfPersons"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                      {...register("numberOfPersons")}
                    />
                    {errors.numberOfPersons && (
                      <p className="text-red-500">{"Number of people is required"}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-6 my-6 justify-between">
                  <div className="w-1/2">
                    <label htmlFor="arrivalDate">
                      Arrival Date <RequiredIndicator />
                    </label>
                    <input
                      min={new Date().toISOString().split("T")[0]}
                      id="arrivalDate"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                      type="date"
                      {...register("arrivalDate")}
                    />
                    {errors.arrivalDate && (
                      <p className="text-sm text-red-500">{errors.arrivalDate.message}</p>
                    )}
                  </div>
                  <div className="w-1/2">
                    <label htmlFor="departureDate">
                      Departure Date <RequiredIndicator />
                    </label>
                    <input
                      min={formattedDate}
                      id="departureDate"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                      type="date"
                      {...register("departureDate")}
                    />
                    {errors.departureDate && (
                      <p className="text-sm text-red-500">{errors.departureDate.message}</p>
                    )}
                  </div>
                </div>

                {/* Add the cost calculation display */}

                <div className="space-y-4 my-6">
                  <div>
                    <label
                      htmlFor="proximityToSwamiJi"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Preferred Proximity to Swami Ji's Kutiya
                      <RequiredIndicator />
                    </label>
                    <select
                      id="proximityToSwamiJi"
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                      {...register("proximityToSwamiJi")}
                    >
                      <option value="">Select preference</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                    {errors.proximityToSwamiJi && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.proximityToSwamiJi.message}
                      </p>
                    )}
                  </div>
                </div>
                <div className="bg-orange-50 p-6 rounded-lg my-6">
                  <div className="text-xl font-bold text-gray-900 mb-2">
                    Total Estimated Cost: ₹
                    {calculateCost(
                      getValues("typeOfKutiya"),
                      Number(getValues("numberOfPersons")),
                      getValues("arrivalDate"),
                      getValues("departureDate"),
                      getValues("proximityToSwamiJi")
                    ).toLocaleString("en-IN")}
                  </div>
                  <p className="text-sm text-gray-600">
                    Based on selected accommodation and duration
                    {getValues("proximityToSwamiJi") === "true" &&
                      " (includes ₹2,000/day for proximity to Swami Ji's Kutiya)"}
                  </p>
                </div>
                <textarea
                  className="w-full mt-1 h-32 rounded-md border-gray-300 shadow-sm focus:border-orange-500 my-6 focus:ring-orange-500"
                  type="textarea"
                  id="specialRequirements"
                  {...register("specialRequirements")}
                  placeholder="Special Requirements"
                />
              </>
            )}

            {/* Step 3:  Group & Volunteering */}
            {step === 2 && (
              <>
                <h2 className="text-lg font-semibold">Group & Volunteering</h2>
                <div className="space-y-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Are You Registering as a Group? <RequiredIndicator />
                      </label>
                      <div className="mt-2 space-x-4">
                        <label className="inline-flex items-center">
                          <input
                            type="radio"
                            className="form-radio text-orange-600"
                            name="isGroup"
                            value="yes"
                            checked={formData.isGroup === "yes"}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                isGroup: e.target.value,
                                groupMembers: e.target.value === "yes" ? [] : [],
                              });
                              setValue("groupRegister", e.target.value === "yes");
                            }}
                          />
                          <span className="ml-2">Yes</span>
                        </label>
                        <label className="inline-flex items-center">
                          <input
                            type="radio"
                            className="form-radio text-orange-600"
                            name="isGroup"
                            value="no"
                            checked={formData.isGroup === "no"}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                isGroup: e.target.value,
                                groupMembers: [],
                              });
                              setValue("groupRegister", false);
                            }}
                          />
                          <span className="ml-2">No</span>
                        </label>
                      </div>
                      {errors.groupRegister && (
                        <p className="mt-1 text-sm text-red-500">Please select an option</p>
                      )}
                    </div>

                    {formData.isGroup === "yes" && (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Group Members
                          </label>
                          <div className="mt-4 space-y-4">
                            {formData.groupMembers.map((member, index) => (
                              <div key={index} className="p-4 border rounded-md bg-gray-50">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                      Name <RequiredIndicator />
                                    </label>
                                    <input
                                      type="text"
                                      required
                                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                                      value={member.name}
                                      onChange={(e) =>
                                        handleMemberChange(index, "name", e.target.value)
                                      }
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                      Age <RequiredIndicator />
                                    </label>
                                    <input
                                      type="number"
                                      required
                                      min="0"
                                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                                      value={member.age}
                                      onChange={(e) =>
                                        handleMemberChange(index, "age", e.target.value)
                                      }
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                      Gender <RequiredIndicator />
                                    </label>
                                    <select
                                      required
                                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                                      value={member.gender}
                                      onChange={(e) =>
                                        handleMemberChange(index, "gender", e.target.value)
                                      }
                                    >
                                      <option value="">Select Gender</option>
                                      <option value="male">Male</option>
                                      <option value="female">Female</option>
                                      <option value="other">Other</option>
                                    </select>
                                  </div>
                                </div>
                                <div className="mt-4">
                                  <label className="block text-sm font-medium text-gray-700">
                                    ID Proof <RequiredIndicator />
                                  </label>
                                  <input
                                    type="file"
                                    required
                                    className="mt-1 block w-full text-sm text-gray-500
                                      file:mr-4 file:py-2 file:px-4
                                      file:rounded-md file:border-0
                                      file:text-sm file:font-semibold
                                      file:bg-orange-50 file:text-orange-700
                                      hover:file:bg-orange-100"
                                    onChange={(e) =>
                                      handleMemberChange(index, "idFile", e.target.files[0])
                                    }
                                    accept=".png,.jpg,.jpeg,.pdf"
                                  />
                                </div>
                                <div className="mt-4 flex justify-end">
                                  <button
                                    type="button"
                                    className="text-red-600 hover:text-red-800"
                                    onClick={() => handleRemoveMember(index)}
                                  >
                                    Remove Member
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                          <button
                            type="button"
                            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-orange-700 bg-orange-100 hover:bg-orange-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                            onClick={() => handleAddMember()}
                          >
                            Add Group Member
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Add a divider */}
                  <hr className="my-8 border-gray-200" />

                  {/* Add Volunteering Section */}
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-gray-800">
                      Volunteering and Participation
                    </h3>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Are You Interested in Volunteering During the Event? <RequiredIndicator />
                      </label>
                      <div className="mt-2 space-x-4">
                        <label className="inline-flex items-center">
                          <input
                            type="radio"
                            className="form-radio text-orange-600"
                            name="isVolunteer"
                            value="yes"
                            checked={formData.isVolunteer === "yes"}
                            onChange={(e) => {
                              setFormData({ ...formData, isVolunteer: e.target.value });
                              setValue("volunteeringRegistration", true);
                            }}
                          />
                          <span className="ml-2">Yes</span>
                        </label>
                        <label className="inline-flex items-center">
                          <input
                            type="radio"
                            className="form-radio text-orange-600"
                            name="isVolunteer"
                            value="no"
                            checked={formData.isVolunteer === "no"}
                            onChange={(e) => {
                              setFormData({ ...formData, isVolunteer: e.target.value });
                              setValue("volunteeringRegistration", false);
                            }}
                          />
                          <span className="ml-2">No</span>
                        </label>
                      </div>
                    </div>

                    {formData.isVolunteer === "yes" && (
                      <div className="space-y-4">
                        <label className="block text-sm font-medium text-gray-700">
                          Areas of Interest <RequiredIndicator />
                        </label>
                        <div className="space-y-2">
                          {[
                            "Food Distribution",
                            "Crowd Management",
                            "Medical Assistance",
                            "Kutiya Management",
                          ].map((area) => (
                            <label key={area} className="flex items-center">
                              <input
                                type="checkbox"
                                className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                                checked={(formData.volunteerAreas || []).includes(area)}
                                onChange={() => handleVolunteerAreaChange(area)}
                              />
                              <span className="ml-2 text-gray-700">{area}</span>
                            </label>
                          ))}
                          <div>
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                                checked={(formData.volunteerAreas || []).includes("Other")}
                                onChange={() => handleVolunteerAreaChange("Other")}
                              />
                              <span className="ml-2 text-gray-700">Other</span>
                            </label>
                            {(formData.volunteerAreas || []).includes("Other") && (
                              <input
                                type="text"
                                className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                                placeholder="Please specify"
                                value={formData.otherVolunteerArea || ""}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    otherVolunteerArea: e.target.value,
                                  })
                                }
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Comments for the Organizers
                      </label>
                      <textarea
                        rows="3"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                        placeholder="Any additional notes or comments for the organizers"
                        value={formData.organizerComments || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, organizerComments: e.target.value })
                        }
                        {...register("commentsForOrganizers")}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
            {/* Step 3: Document Upload */}
            {step === 3 && (
              <>
                <div className="space-y-6 mt-6">
                  <h3 className="text-xl font-semibold text-gray-800">ID Proof and Verification</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Existing ID Proof section */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Primary ID Proof <RequiredIndicator />
                      </label>
                      <input
                        type="file"
                        {...register("idDoc")}
                        className="mt-1 block w-full text-sm text-gray-500
                          file:mr-4 file:py-2 file:px-4
                          file:rounded-md file:border-0
                          file:text-sm file:font-semibold
                          file:bg-orange-50 file:text-orange-700
                          hover:file:bg-orange-100"
                        accept=".png,.jpg,.jpeg,.pdf"
                        onChange={(e) =>
                          setFormData({ ...formData, primaryIdFile: e.target.files[0] })
                        }
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        Accepted: Aadhaar, Passport, Voter ID
                      </p>
                      {errors.idDoc && (
                        <p className="mt-1 text-sm text-red-500">Document upload is required</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        ID Number <RequiredIndicator />
                      </label>
                      <input
                        type="text"
                        {...register("documentId")}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                        value={formData.primaryIdNumber}
                        onChange={(e) =>
                          setFormData({ ...formData, primaryIdNumber: e.target.value })
                        }
                      />
                      {errors.documentId && (
                        <p className="mt-1 text-sm text-red-500">Document ID is required</p>
                      )}
                    </div>
                  </div>
                  <div className="border-t border-gray-300 h-1" />
                  {/* Add Payment Details Section */}
                  <div className="mt-8">
                    <div className="space-y-4">
                      <h3 className="text-xl font-semibold text-gray-800">Payment Details</h3>

                      <div className="bg-orange-50 p-6 rounded-lg">
                        <div className="text-xl font-bold text-gray-900 mb-2">
                          Total Estimated Cost: ₹{(formData.totalCost || 0).toLocaleString("en-IN")}
                        </div>
                        <p className="text-sm text-gray-600">
                          Final cost may vary based on actual stay duration and any additional
                          services
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Preferred Payment Method <RequiredIndicator />
                        </label>
                        <select
                          {...register("paymentMethod")}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                          value={formData.paymentMethod || ""}
                          onChange={(e) =>
                            setFormData({ ...formData, paymentMethod: e.target.value })
                          }
                        >
                          <option value="">Select Payment Method</option>
                          <option value="upi">UPI</option>
                          <option value="credit">Credit Card</option>
                          <option value="debit">Debit Card</option>
                          <option value="netbanking">Net Banking</option>
                        </select>
                        {errors.paymentMethod && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.paymentMethod.message}
                          </p>
                        )}
                      </div>

                      <div className="flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            type="checkbox"
                            {...register("paymentAcknowledgment")}
                            className="focus:ring-orange-500 h-4 w-4 text-orange-600 border-gray-300 rounded"
                            checked={formData.paymentAcknowledgment || false}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                paymentAcknowledgment: e.target.checked,
                              })
                            }
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label className="font-medium text-gray-700">
                            Payment Acknowledgment <RequiredIndicator />
                          </label>
                          <p className="text-gray-500">
                            I understand that a payment link will be sent via SMS/Email upon
                            confirmation of my registration, and the booking will only be confirmed
                            post-payment.
                          </p>
                        </div>
                      </div>
                      {errors.paymentAcknowledgment && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.paymentAcknowledgment.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="border-t border-gray-300 h-1" />

                  {/* Terms and Conditions Section */}
                  <div className="mt-8">
                    <div className="space-y-4">
                      <h3 className="text-xl font-semibold text-gray-800">Terms and Conditions</h3>

                      <div className="bg-gray-50 p-4 rounded-md text-sm text-gray-700 space-y-2">
                        <h4 className="font-medium">Key Terms:</h4>
                        <ul className="list-disc pl-5 space-y-1">
                          <li>50% advance payment is required for booking confirmation</li>
                          <li>Free cancellation up to 7 days before check-in</li>
                          <li>50% refund for cancellations between 3-7 days before check-in</li>
                          <li>No refund for cancellations within 3 days of check-in</li>
                          <li>Check-in: 2:00 PM, Check-out: 11:00 AM</li>
                        </ul>
                      </div>

                      <div className="flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            type="checkbox"
                            {...register("termsAccepted")}
                            className="focus:ring-orange-500 h-4 w-4 text-orange-600 border-gray-300 rounded"
                            checked={formData.termsAccepted}
                            onChange={(e) =>
                              setFormData({ ...formData, termsAccepted: e.target.checked })
                            }
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label className="font-medium text-gray-700">
                            Accept Terms and Conditions <RequiredIndicator />
                          </label>
                          <p className="text-gray-500">
                            I confirm that the information provided is accurate, and I agree to the
                            terms and conditions of Prabhu Premi Sangh's Kumbh Mahaparv program.
                          </p>
                        </div>
                      </div>
                      {errors.termsAccepted && (
                        <p className="mt-1 text-sm text-red-500">
                          You must accept the terms and conditions
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Navigation Buttons - Make them full width on mobile */}
            <div className="flex flex-col sm:flex-row gap-4 sm:justify-between mt-6 pt-4 border-t">
              {step > 0 && (
                <button
                  type="button"
                  onClick={onPrevious}
                  className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                >
                  Back
                </button>
              )}
              {step < 3 ? (
                <button
                  type="button"
                  onClick={onNext}
                  className="w-full sm:w-auto px-6 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  className="ml-auto px-8 py-3 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 font-medium"
                >
                  Complete Registration
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MultiStepForm;
